import { useEffect, useState } from "react";
import type { AiChatPeriod } from "@/config/aiChat";
import { getAiChatState } from "@/lib/aiChat.functions";
import {
  getAnonId,
  getLastAiChatQuota,
  publishAiChatQuota,
  subscribeAiChatQuota,
} from "@/components/ai/aiChatBus";

export type AiChatQuota = {
  remaining: number;
  limit: number;
  period: AiChatPeriod;
  signedIn: boolean;
  isAdmin: boolean;
};

/** Hämtar hur många AI-frågor användaren har kvar. `enabled=false` hoppar över anropet. */
export const useAiChatQuota = (enabled = true) => {
  const [quota, setQuota] = useState<AiChatQuota | null>(
    () => (getLastAiChatQuota() as AiChatQuota | null) ?? null,
  );

  useEffect(() => {
    if (!enabled) return;
    const current = getLastAiChatQuota();
    if (current) setQuota(current as AiChatQuota);
    return subscribeAiChatQuota((next) => setQuota(next as AiChatQuota));
  }, [enabled]);

  useEffect(() => {
    if (!enabled || getLastAiChatQuota()) return;
    let active = true;
    void getAiChatState({ data: { anonId: getAnonId() } })
      .then((state) => {
        if (!active) return;
        const next: AiChatQuota = {
          remaining: state.remaining,
          limit: state.limit,
          period: state.period,
          signedIn: state.signedIn,
          isAdmin: state.isAdmin,
        };
        setQuota(next);
        publishAiChatQuota(next);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [enabled]);

  return quota;
};
